import { useNotificationStore } from "../stores/notificationStore";
import { formatUnit } from "../utils";

export function useNotifications() {
  const {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  } = useNotificationStore();

  return {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
}

// Hook específico para notificaciones de carrito
export function useCartNotifications() {
  const { showSuccess, showError, showWarning, showInfo } = useNotifications();

  const notifyAddToCart = (
    productName: string,
    quantity: number = 1,
    unitOfMeasurement?: string | null
  ) => {
    const unitText = formatUnit(unitOfMeasurement);
    const isPlural = quantity > 1;
    
    let message: string;
    if (isPlural) {
      message = `${quantity} ${unitText} de ${productName} se agregaron al carrito`;
    } else {
      // Para singular, usar la unidad en singular
      const unitTextSingular = unitOfMeasurement === "m²" || unitOfMeasurement === "m2"
        ? "m²"
        : unitOfMeasurement === "caja" || unitOfMeasurement === "cajas"
        ? "caja"
        : unitOfMeasurement === "unidad" || unitOfMeasurement === "unidades"
        ? "unidad"
        : unitOfMeasurement || "unidad";
      
      message = `${productName} (${quantity} ${unitTextSingular}) se agregó al carrito`;
    }
    
    showSuccess(
      "Producto agregado",
      message,
      { duration: 3000 }
    );
  };

  const notifyRemoveFromCart = (productName: string) => {
    showInfo(
      "Producto removido",
      `${productName} se removió del carrito`,
      { duration: 3000 }
    );
  };

  // Removido notifyUpdateQuantity - muy molesto si se cambia mucho

  const notifyClearCart = () => {
    showWarning(
      "Carrito vaciado",
      "Se removieron todos los productos del carrito",
      { duration: 4000 }
    );
  };

  const notifyCartError = (message: string) => {
    showError("Error en el carrito", message);
  };

  return {
    notifyAddToCart,
    notifyRemoveFromCart,
    notifyClearCart,
    notifyCartError,
  };
}

// Hook específico para notificaciones de admin
export function useAdminNotifications() {
  const { showSuccess, showError, showWarning } = useNotifications();

  const notifyProductCreated = (productName: string) => {
    showSuccess(
      "Producto creado",
      `${productName} se creó exitosamente`,
      { duration: 4000 }
    );
  };

  const notifyProductUpdated = (productName: string) => {
    showSuccess(
      "Producto actualizado",
      `${productName} se actualizó exitosamente`,
      { duration: 4000 }
    );
  };

  const notifyProductDeleted = (productName: string) => {
    showWarning(
      "Producto eliminado",
      `${productName} se eliminó exitosamente`,
      { duration: 4000 }
    );
  };

  const notifyCategoryCreated = (categoryName: string) => {
    showSuccess(
      "Categoría creada",
      `${categoryName} se creó exitosamente`,
      { duration: 4000 }
    );
  };

  const notifyCategoryUpdated = (categoryName: string) => {
    showSuccess(
      "Categoría actualizada",
      `${categoryName} se actualizó exitosamente`,
      { duration: 4000 }
    );
  };

  const notifyCategoryDeleted = (categoryName: string) => {
    showWarning(
      "Categoría eliminada",
      `${categoryName} se eliminó exitosamente`,
      { duration: 4000 }
    );
  };

  const notifyAdminError = (action: string, message: string) => {
    showError(`Error en ${action}`, message);
  };

  const notifySaveSuccess = (entity: string) => {
    showSuccess(
      "Guardado exitoso",
      `Los cambios de ${entity} se guardaron correctamente`,
      { duration: 4000 }
    );
  };

  return {
    notifyProductCreated,
    notifyProductUpdated,
    notifyProductDeleted,
    notifyCategoryCreated,
    notifyCategoryUpdated,
    notifyCategoryDeleted,
    notifyAdminError,
    notifySaveSuccess,
  };
}
